import React from "react";

import { useSelector } from "react-redux";
import { Row, Col, Card, Button } from "react-bootstrap";
import { Redirect, useParams } from "react-router-dom";
import Datatable from "react-data-table-component";
import { debounce } from "lodash";

import { fetchData, getToolTitleFromToolName } from "../../helpers";
import APIComponent from "../../components/API";
import core from "../../vendors/core-api";
import { LayoutSplashScreen } from "../../../_metronic/layout";
import TitleHelmet from "../../partials/TitleHelmet";
import { HeaderTextWithActions } from "../../components/Base";
import Breadcrumbs from "../../components/Breadcrumbs";
import AdminToolsStoreToolModal from "./AdminToolsStoreToolModal";
import PacmanLoading from "../../components/widgets/PacmanLoading/PacmanLoading";
import PaginationBar from "../../components/PaginationBar";
import { ADMINTOOLS_FETCH_LIMIT } from "../../constants";

export default function Page() {
  const { user } = useSelector((state) => state.auth);
  const { tool_name = "" } = useParams();
  return <MainComponent user={user} toolName={tool_name} />;
}

const utilizeFocus = () => {
  const ref = React.createRef();
  const setFocus = () => {
    ref.current && ref.current.focus();
  };

  return { setFocus, ref };
};

class MainComponent extends APIComponent {
  constructor(props) {
    super(props);

    this.state = {
      tools: "",
      showModal: false,
      showSplashScreen: false,
      redirectTo: "",
      toolName: this.props.toolName,
      showStoreToolModal: false,
      selectedData: "",
      pageNumber: 1,
      title: getToolTitleFromToolName(this.props.toolName),
      searchText: "",
      isLoading: false,
    };

    this.api = core("pu").get();
    const { pageNumber } = this.state;
    if (this.props.toolName === "restore_store") {
      this.data_url = `/admintools/stores/deleted?page=${pageNumber}`;
    } else {
      this.data_url = `/admintools/stores?page=${pageNumber}`;
    }

    this.inputFocus = utilizeFocus();
  }

  handleRefreshStore = async () => {
    let data = {};
    const { pageNumber } = this.state;
    this.setState({ isLoading: true });
    if (this.props.toolName === "restore_store") {
      ({ data } = await fetchData(
        "get",
        `/admintools/stores/deleted?page=${pageNumber}`,
      ));
    } else {
      ({ data } = await fetchData(
        "get",
        `/admintools/stores?page=${pageNumber}`,
      ));
    }
    this.setState({ data, isLoading: false });
  };

  handleRefreshStoreWithSearch = async (searchText, pageNumber = 1) => {
    let data = {};
    this.setState({ isLoading: true });
    if (this.props.toolName === "restore_store") {
      ({ data } = await fetchData(
        "get",
        `/admintools/stores/deleted?searchKey=${searchText}&page=${pageNumber}`,
      ));
    } else {
      ({ data } = await fetchData(
        "get",
        `/admintools/stores?searchKey=${searchText}&page=${pageNumber}`,
      ));
    }
    this.setState({ data, isLoading: false });
  };

  handleChangeSearchText = (searchText) => {
    this.setState({ searchText });
    this.debounceChangeSearchText(searchText);
  };

  debounceChangeSearchText = debounce(async (searchText) => {
    await this.handleRefreshStoreWithSearch(searchText);
  }, 500);

  handleClickStore = (toolName, data) => {
    this.setState({ showStoreToolModal: true, selectedData: data });
  };

  handleSelectPage = async (pageNumber = 1, pagesArr = []) => {
    const { searchText } = this.state;
    let page = pageNumber;
    if (page <= 0) {
      page = 1;
    }
    if (page > pagesArr.length) {
      page = pagesArr.length;
    }
    const result = await this.handleRefreshStoreWithSearch(
      searchText,
      pageNumber,
    );
    this.setState({ pageNumber });
  };

  render() {
    const {
        data,
        data_status = "",
        toolName = "",
        selectedData = "",
        searchText = "",
        pageNumber = 1,
      } = this.state,
      isLoading = data_status !== "fetched";

    const limit = ADMINTOOLS_FETCH_LIMIT;

    let pagesArr = [];
    if (data && data.items && data.items.length) {
      for (let i = 0; i * limit < data.totalCount; i++) {
        pagesArr.push(i);
      }
    }

    return (
      <>
        <div className="col-md-9 col-sm-12">
          <Breadcrumbs>
            <Breadcrumbs.Item text={`Admin Tools`} link={`/admin/tools`} />
            <Breadcrumbs.Item
              text={getToolTitleFromToolName(this.props.toolName)}
              link={`/admin/tools/stores`}
              active="true"
            />
          </Breadcrumbs>
        </div>

        <TitleHelmet title={this.state.title} />

        {this.state.showSplashScreen ? <LayoutSplashScreen /> : <></>}

        {this.state.redirectTo !== "" ? (
          <Redirect to={this.state.redirectTo} />
        ) : (
          <></>
        )}

        {this.state.selectedData ? (
          <AdminToolsStoreToolModal
            handleRefreshStore={this.handleRefreshStore}
            show={this.state.showStoreToolModal}
            toolName={toolName}
            onHide={() => {
              this.setState({ showStoreToolModal: false });
            }}
            data={selectedData}
          />
        ) : (
          <></>
        )}

        <HeaderTextWithActions title={this.state.title} />

        {this.state.isLoading || isLoading ? (
          <>
            <div className="d-flex justify-content-center align-items-center h-100">
              <PacmanLoading />
            </div>
          </>
        ) : (
          <>
            <Card className="mt-4">
              <Card.Body>
                <SearchBar
                  searchText={searchText}
                  handleChangeSearchText={this.handleChangeSearchText}
                  inputFocus={this.inputFocus}
                />
                {data.items && data.items.length > 0 ? (
                  <>
                    <h3 className="align-self-start mb-0 pb-0 mt-4">
                      Select Store
                    </h3>
                  </>
                ) : (
                  <>
                    <div className="mt-4 text-center">No records found.</div>
                  </>
                )}
                <StoreList
                  data={data}
                  toolName={toolName}
                  handleClickStore={this.handleClickStore}
                />
                {data.items && data.items.length > 0 && (
                  <div className="position-sticky" style={{ left: 0 }}>
                    <PaginationBar
                      pagesArr={pagesArr}
                      totalResults={data.totalCount}
                      rows={limit}
                      pageNum={pageNumber}
                      limit={limit}
                      onSelectPage={this.handleSelectPage}
                    />
                  </div>
                )}
              </Card.Body>
            </Card>
          </>
        )}
      </>
    );
  }
}

function SearchBar({
  searchText = "",
  handleChangeSearchText,
  inputFocus = {},
}) {
  return (
    <>
      <Row className="w-100">
        <Col>
          <div className="d-flex flex-column w-100 align-items-center">
            <div
              style={{ width: "45vw" }}
              className="input-group input-group-lg"
            >
              <div className="input-group-prepend">
                <span className="input-group-text">
                  <i className="flaticon-search"></i>
                </span>
              </div>
              <input
                autoFocus
                ref={inputFocus.ref}
                type="text"
                className="form-control"
                placeholder="Search Store Name"
                value={searchText}
                onChange={(e) => {
                  handleChangeSearchText(e.target.value);
                }}
              />
            </div>
          </div>
        </Col>
      </Row>
    </>
  );
}

function StoreList({ data, toolName, handleClickStore }) {
  const filteredItems = data.items;

  return (
    <>
      {toolName === "restore_store" ? (
        <>
          {filteredItems.map((row) => {
            return (
              <DeletedStoreTableCard
                key={row._id}
                data={row}
                toolName={toolName}
                handleClickStore={handleClickStore}
              />
            );
          })}
        </>
      ) : (
        <>
          {filteredItems.map((row) => {
            return (
              <StoreTableCard
                key={row._id}
                data={row}
                toolName={toolName}
                handleClickStore={handleClickStore}
              />
            );
          })}
        </>
      )}
    </>
  );
}

function StoreTableCard({ data, toolName, handleClickStore }) {
  return (
    <>
      <div>
        <Col className="w-100">
          <Card
            style={{ width: "65vw" }}
            className="my-3 cursor-pointer"
            onClick={() => handleClickStore(toolName, data)}
          >
            <Card.Body>
              <div className="d-flex flex-row align-items-center justify-content-space-between">
                <div className="">
                  <img
                    src={data.brand && data.brand.brand_logo}
                    alt="image"
                    style={{
                      width: "5.5rem",
                    }}
                  />
                </div>
                <div className="ml-4 mr-2 d-flex flex-column flex-grow-1 align-items-start">
                  <h4>{`${data.name}`}</h4>
                  <h6>{`Brand Name: ${
                    data.brand && data.brand.name ? data.brand.name : `-`
                  }`}</h6>
                  <h6>{`Brand URL: ${
                    data.brand && data.brand.brand_url
                      ? data.brand.brand_url
                      : `-`
                  }`}</h6>
                  <h6>{`Store URL: ${data.store_url}`}</h6>
                  <h6>{`Location: ${data.location}`}</h6>
                </div>
                <div className="ml-4">
                  <Button
                    type="submit"
                    className="btn-block text-uppercase font-weight-bold"
                  >
                    Select
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </div>
    </>
  );
}

function DeletedStoreTableCard({ data, toolName, handleClickStore }) {
  return (
    <>
      <div>
        <Col className="w-100">
          <Card
            style={{ width: "65vw" }}
            className="my-3 cursor-pointer"
            onClick={() => handleClickStore(toolName, data)}
          >
            <Card.Body>
              <div className="d-flex flex-row align-items-center justify-content-space-between">
                <div className="ml-4 d-flex flex-column flex-grow-1 align-items-start">
                  <h4>{`Store Name: ${data.origin_key}`}</h4>
                  <h6>{`Store Id: ${data.origin_id}`}</h6>
                  <h6>{`Date Deleted: ${data.date_created}`}</h6>
                </div>
                <div className="ml-4">
                  <Button
                    type="submit"
                    className="btn-block text-uppercase font-weight-bold"
                  >
                    Select
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </div>
    </>
  );
}
